import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getProductBySlug } from '../api/products'
import { AUTH_PATHS } from '../constants/auth'
import { CART_ADD_INTENT, CART_PATHS, type CartAddIntent } from '../constants/cart'
import { QUERY_KEYS } from '../constants/query'
import { toApiMessage } from '../helpers/api-error'
import {
  cartLineCompareAt,
  cartQuantityForProduct,
  findCartLine,
  type CartLineInput,
} from '../helpers/cart'
import { discountPercent } from '../helpers/format'
import {
  mapProductDetail,
  type ProductContentView,
  type ProductViewModel,
} from '../helpers/mapCatalog'
import {
  pickGalleryImageId,
  resolveVariantGallery,
} from '../helpers/productGallery'
import { useAuth } from './useAuth'
import { useCart } from './useCart'
import { useDeliveryLocation } from './useDeliveryLocation'
import { useWishlist } from './useWishlist'

type ProductPageState = {
  product: ProductViewModel | null
  loading: boolean
  error: string | null
  imageId: string
  setImageId: (id: string) => void
  galleryImages: ReturnType<typeof resolveVariantGallery>
  stemId: string
  setStemId: (id: string) => void
  wrapperId: string
  setWrapperId: (id: string) => void
  addonIds: string[]
  toggleAddon: (id: string) => void
  wishlisted: boolean
  toggleWishlist: () => void
  wishlistBusy: boolean
  compareAt: number | null
  total: number
  savingsPercent: number
  selectedSku: string | null
  /** PDP info for the selected stem (resolvedContent / product defaults). */
  selectedContent: ProductContentView | null
  addToCart: () => void
  buyNow: () => void
  justAdded: boolean
  productInCartQty: number
  selectedConfigInCartQty: number
}

export function useProductPage(slug: string | undefined): ProductPageState {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { addItem, lines } = useCart()
  const { location, isModalOpen, openModal } = useDeliveryLocation()
  const pendingIntent = useRef<CartAddIntent | null>(null)
  const awaitingLocation = useRef(false)
  const { isWishlisted, toggle } = useWishlist()
  const productQuery = useQuery({
    queryKey: QUERY_KEYS.product(slug ?? ''),
    queryFn: () => getProductBySlug(slug!),
    enabled: Boolean(slug),
  })
  const product = productQuery.data ? mapProductDetail(productQuery.data) : null
  const loading =
    Boolean(slug) && productQuery.isPending && !productQuery.data && !productQuery.error
  const error = !slug
    ? 'Missing product slug'
    : productQuery.error
      ? toApiMessage(productQuery.error, 'Failed to load product')
      : null
  const [imageId, setImageId] = useState('')
  const [stemId, setStemId] = useState('')
  const [wrapperId, setWrapperId] = useState('')
  const [addonIds, setAddonIds] = useState<string[]>([])
  const [wishlistBusy, setWishlistBusy] = useState(false)
  const [justAdded, setJustAdded] = useState(false)

  useEffect(() => {
    if (!product) return
    const defaultStem =
      product.stemOptions.find((option) => option.isDefault)?.id ??
      product.stemOptions[0]?.id ??
      ''
    setStemId(defaultStem)
    const defaultStemOption = product.stemOptions.find(
      (option) => option.id === defaultStem,
    )
    setImageId(defaultStemOption?.imageIds?.[0] ?? product.images[0]?.id ?? '')
    setWrapperId(product.wrapperOptions[0]?.id ?? '')
    setAddonIds([])
    setJustAdded(false)
  }, [product?.id])

  useEffect(() => {
    if (!justAdded) return
    const timer = window.setTimeout(() => setJustAdded(false), 6000)
    return () => window.clearTimeout(timer)
  }, [justAdded])

  const stem = useMemo(
    () => product?.stemOptions.find((option) => option.id === stemId) ?? product?.stemOptions[0],
    [product, stemId],
  )
  const galleryImages = useMemo(
    () =>
      product
        ? resolveVariantGallery(product.images, product.sharedImageIds, stem)
        : [],
    [product, stem],
  )
  const wrapper = useMemo(
    () =>
      product?.wrapperOptions.find((option) => option.id === wrapperId) ??
      product?.wrapperOptions[0],
    [product, wrapperId],
  )
  const selectedAddons = useMemo(
    () => product?.addonOptions.filter((option) => addonIds.includes(option.id)) ?? [],
    [product, addonIds],
  )

  const compareAt = stem?.compareAtPrice ?? null
  const addonTotal = selectedAddons.reduce((sum, option) => sum + option.price, 0)
  const total = (stem?.price ?? 0) + (wrapper?.price ?? 0) + addonTotal
  const savingsPercent =
    compareAt != null && stem ? discountPercent(compareAt, stem.price) : 0
  const selectedContent = stem?.content ?? product?.content ?? null

  const productInCartQty = product
    ? cartQuantityForProduct(lines, product.id)
    : 0

  const selectedConfigInCartQty = useMemo(() => {
    if (!product || !stem) return 0
    const match = findCartLine(lines, {
      productId: product.id,
      variantId: stem.id,
      wrapperId: wrapper?.id ?? null,
      addonIds: selectedAddons.map((option) => option.id),
    })
    return match?.quantity ?? 0
  }, [lines, product, stem, wrapper, selectedAddons])

  useEffect(() => {
    if (!product || galleryImages.length === 0) return
    setImageId((current) => pickGalleryImageId(galleryImages, current))
  }, [product, galleryImages])

  function selectStem(id: string) {
    setStemId(id)
    if (!product) return
    const option = product.stemOptions.find((item) => item.id === id)
    const nextGallery = resolveVariantGallery(
      product.images,
      product.sharedImageIds,
      option,
    )
    const preferred =
      option?.imageIds?.[0] ??
      product.sharedImageIds[0] ??
      nextGallery[0]?.id ??
      ''
    setImageId(pickGalleryImageId(nextGallery, preferred))
  }

  function toggleAddon(id: string) {
    setAddonIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    )
  }

  async function toggleWishlist() {
    if (!product) return
    if (!isAuthenticated) {
      navigate(AUTH_PATHS.login, {
        state: { from: `/product/${product.slug}` },
      })
      return
    }
    if (wishlistBusy) return
    setWishlistBusy(true)
    try {
      await toggle(product.id)
    } finally {
      setWishlistBusy(false)
    }
  }

  function buildCartLine(): CartLineInput | null {
    if (!product || !stem) return null
    const activeImage =
      galleryImages.find((image) => image.id === imageId) ?? galleryImages[0]
    return {
      productId: product.id,
      productSlug: product.slug,
      name: product.name,
      imageUrl: activeImage?.url ?? null,
      variantId: stem.id,
      variantLabel: stem.label,
      wrapperId: wrapper?.id ?? null,
      wrapperLabel: wrapper?.label ?? null,
      addonIds: selectedAddons.map((option) => option.id),
      addonLabels: selectedAddons.map((option) => option.label),
      unitPrice: total,
      expressEligible: product.isExpressEligible,
      compareAtPrice: cartLineCompareAt(
        stem.compareAtPrice ?? null,
        stem.price,
        (wrapper?.price ?? 0) + addonTotal,
      ),
      quantity: 1,
    }
  }

  const commitAddRef = useRef<() => boolean>(() => false)
  commitAddRef.current = () => {
    const line = buildCartLine()
    if (!line) return false
    addItem(line)
    setJustAdded(true)
    return true
  }

  useEffect(() => {
    if (!awaitingLocation.current || isModalOpen) return
    const intent = pendingIntent.current
    pendingIntent.current = null
    awaitingLocation.current = false
    if (!location || !intent) return
    if (!commitAddRef.current()) return
    if (intent === CART_ADD_INTENT.buy) navigate(CART_PATHS.page)
  }, [isModalOpen, location, navigate])

  function requestAdd(intent: CartAddIntent) {
    if (!buildCartLine()) return
    if (!location) {
      pendingIntent.current = intent
      awaitingLocation.current = true
      openModal()
      return
    }
    if (!commitAddRef.current()) return
    if (intent === CART_ADD_INTENT.buy) navigate(CART_PATHS.page)
  }

  function addToCart() {
    requestAdd(CART_ADD_INTENT.add)
  }

  function buyNow() {
    requestAdd(CART_ADD_INTENT.buy)
  }

  return {
    product,
    loading,
    error,
    imageId,
    setImageId,
    galleryImages,
    stemId,
    setStemId: selectStem,
    wrapperId,
    setWrapperId,
    addonIds,
    toggleAddon,
    wishlisted: product ? isWishlisted(product.id) : false,
    toggleWishlist,
    wishlistBusy,
    compareAt,
    total,
    savingsPercent,
    selectedSku: stem?.sku?.trim() || null,
    selectedContent,
    addToCart,
    buyNow,
    justAdded,
    productInCartQty,
    selectedConfigInCartQty,
  }
}
